import { Button, Tooltip } from 'antd';
import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import { getSourceString } from '../../../helpers/StringHelper';
import styled from 'styled-components';
import { EyeOutlined } from '@ant-design/icons';
import moment from 'moment';
import { formatClassname } from 'datastake-daf/src/helpers/ClassesHelper';

const ViewStyle = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;

  .ghosted {
    border: 0px;

    span {
      text-decoration: underline;
    }
  }
`;

export const linkingColumns = (t, module = 'pme') => [
  {
    title: t('Type of Subject'),
    dataIndex: 'type',
    key: 'type',
    show: true,
    project: true,
    search: true,
    render: type => t(type),
    searchType: 'dropdown',
  },
  {
    title: t('System ID'),
    dataIndex: 'datastakeId',
    key: 'datastakeId',
    show: true,
    project: true,
    sorter: (a, b) => ('' + a.id).localeCompare(b.id),
    render: (id, item) => {
      let link = '';
      switch (item.type) {
        case 'stakeholder':
          link = `/app/${module}/stakeholders/${id}`;
          break;
        case 'event':
          link = `/app/${module}/events/${id}`;
          break;
        case 'location':
          link = `/app/${module}/locations/${id}`;
          break;
        case 'lir':
          link = `/app/${module}/lir/${id}`;
          break;
        default:
          link = '#';
          break;
      }
      return <NavLink className="projectLink" to={link}>
        {id}
      </NavLink>
    },
    search: true,
  },
  {
    title: t('Name'),
    dataIndex: 'name',
    key: 'name',
    show: true,
    project: true,
    render: (name) => name,
    search: true,
  },
  {
    title: t('Connected as'),
    dataIndex: 'usedAs',
    key: 'usedAs',
    show: true,
    project: true,
    render: (used) => {
      const language = localStorage.getItem('datastakeLng') || 'en';
      return used[language];
    },
    search: true,
  },
];

export default function getColumns(
  t = s => s,
  module = 'pme',
  view = 'stakeholder',
  filterBy = 'show',
  sourceId,
  expandedRows = [],
  activeFilters = {}
) {
  const cols = [
    {
      title: t('ID'),
      dataIndex: 'id',
      key: 'datastakeId',
      overview: true,
      ellipsis: true,
      show: true,
      project: true,
      publication: true,
      defaultSortOrder: activeFilters.sortBy === 'datastakeId' ? activeFilters.sortDir : undefined,
      sorter: () => 0 + 0,
      render: (id, item, index) => {
        const isScp = view === 'scp' || (item.category === 'corporation' && item.economicSector === 'miningMineralTrade');
        // TO-DO fix this
        const isMineOperator = isScp && view === 'mine-operators';

        const editView = isMineOperator ? 'mine-operators' : isScp ? 'scp' : 'stakeholder';
        const redirectUrl = isScp ? (item.children)
          ? `/app/${module}/analysis/${editView}/${id}` : `/app/${module}/analysis/${editView}/${id}/${item.authorId}`
          : (item.isChild || item.children)
            ? `/app/${module}/view/${editView}/${id}?sourceId=${item.authorId}`
            : `/app/${module}/view/${editView}/${id}`;
        // item.isChild && `/app/${module}/analysis/${editView}/${item.id}?defaultSources=${item.authorId}`

        const par = (expandedRows || []).find((s) => s.datastakeId === id || s.id === id);
        return sourceId === 'overview' && item.children ? id : (
          <div style={{ display: 'flex' }}>
            {par && (
              <>
                <div
                  style={{
                    position: 'absolute',
                    left: 15,
                    width: 1,
                    top: 0,
                    height: index === par.children.length - 1 ? '50%' : '100%',
                    background: '#B5B5B5',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    left: 15,
                    height: 1,
                    width: 20,
                    top: '50%',
                    background: '#B5B5B5',
                  }}
                />
              </>
            )}
            <NavLink
              className="projectLink"
              to={redirectUrl}
              {...(filterBy === 'publication' ? {
                target: "_blank"
              } : {})}
            >
              {id}
            </NavLink>
          </div>
        )
      },
      search: true,
      width: 160,
      pubblication: true,
    },
    {
      title: t('Name'),
      dataIndex: 'name',
      key: 'name',
      defaultSortOrder: activeFilters.sortBy === 'name' ? activeFilters.sortDir : undefined,
      sorter: () => 0 + 0,
      overview: true,
      show: true,
      project: true,
      publication: true,
      primarySearch: true,
      render: (title) => <Tooltip title={title}>{title}</Tooltip>,
      ellipsis: true,
    },
    {
      title: t('Position'),
      dataIndex: 'position',
      key: 'position',
      overview: true,
      show: true,
      project: true,
      publication: true,
      primarySearch: false,
      render: (title) => <Tooltip title={title}>{title}</Tooltip>,
      ellipsis: true,
      removeFromView: ['stakeholders'],
    },
    {
      title: t('Legal Form'),
      dataIndex: 'subCategory',
      key: 'subCategory',
      overview: true,
      show: true,
      project: true,
      search: true,
      publication: true,
      searchType: 'dropdown',
      defaultSortOrder: activeFilters.sortBy === 'subCategory' ? activeFilters.sortDir : undefined,
      sorter: () => 0 + 0,
      render: (title) => <Tooltip title={title}>{title}</Tooltip>,
      ellipsis: true
    },
    {
      title: t('Sources'),
      dataIndex: 'sourcesLen',
      key: 'sourcesLen',
      overview: true,
      search: true,
    },
    {
      title: t('Country'),
      dataIndex: 'country',
      key: 'country',
      show: true,
      render: (title) => <Tooltip title={title}>{title}</Tooltip>,
      ellipsis: true,
      project: true,
      search: true,
      publication: true,
      sorter: () => 0 + 0,
      defaultSortOrder: activeFilters.sortBy === 'country' ? activeFilters.sortDir : undefined,
      searchType: 'dropdown',
    },
    {
      title: t('Province'),
      dataIndex: 'province',
      key: 'province',
      // sorter: () => 0 + 0,
      // defaultSortOrder: activeFilters.sortBy === 'province' ? activeFilters.sortDir : undefined,
      show: true,
      project: true,
      publication: true,
      search: true,
      render: (title) => <Tooltip title={title}>{title}</Tooltip>,
      ellipsis: true,
    },
    {
      title: t('Territory'),
      dataIndex: 'territory',
      key: 'territory',
      // sorter: () => 0 + 0,
      show: true,
      project: true,
      render: (title) => <Tooltip title={title}>{title}</Tooltip>,
      ellipsis: true,
      // defaultSortOrder: activeFilters.sortBy === 'territory' ? activeFilters.sortDir : undefined,
      publication: false,
      search: true,
    },
    {
      title: t('Version'),
      dataIndex: 'version',
      key: 'version',
      show: false,
      publication: true,
      ellipsis: true,
    },
    // {
    //   title: t('Sub Category'),
    //   dataIndex: 'subCategory',
    //   key: 'subCategory',
    //   className: 'd-none',
    //   render: (title) => <Tooltip title={title}>{title}</Tooltip>,
    //   ellipsis: true,
    //   show: true,
    //   project: true,
    //   search: true,
    //   publication: true,
    //   searchType: 'dropdown',
    // },
    {
      title: t('Author'),
      dataIndex: 'author',
      key: 'author',
      show: false,
      project: true,
      search: true,
      publication: false,
      ellipsis: true,
      render: (author, item) => {
        const title = getSourceString(author, item);
        return <Tooltip title={title}>
          {title}
        </Tooltip>;
      }
    },
    {
      title: t('Last Update'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      show: true,
      project: true,
      search: false,
      publication: true,
      ellipsis: true,
      render: (updatedAt, item) => {
        const date = item.publishedAt || updatedAt;
        const title = date ? moment(date).format('DD MMM YYYY') : '';
        return <Tooltip title={title}>{title}</Tooltip>
      }
    },
    {
      title: '',
      dataIndex: 'actions',
      key: 'actions',
      show: false,
      project: true,
      publication: false,
      render: (_, item) => {
        const isScp = view === 'scp' || (item.category === 'corporation' && item.economicSector === 'miningMineralTrade');
        // TO-DO fix this
        const isMineOperator = isScp && view === 'mine-operators';

        const editView = isMineOperator ? 'mine-operators' : isScp ? 'scp' : 'stakeholder';
        const pathname = isScp ? (item.children)
          ? `/app/${module}/analysis/${editView}/${item.id}`
          : `/app/${module}/analysis/${editView}/${item.id}/${item.authorId}`
          : (item.isChild || item.children)
            ? `/app/${module}/view/${editView}/${item.id}?sourceId=${item.authorId}`
            : `/app/${module}/view/${editView}/${item.id}`;

        // item.isChild && `/app/${module}/analysis/${editView}/${item.id}?defaultSources=${item.authorId}`

        return (
          <ViewStyle className="view-btn-scl-table">
            <Link to={pathname}>
              <Button
                className={formatClassname([
                  item.children ? 'primary' : 'ghosted',
                  'no-ln-height',
                ])}
                type={item.children ? 'primary' : 'ghost'}
                icon={<EyeOutlined />}
              >
                {t('View')}
              </Button>
            </Link>
          </ViewStyle>
        )
      }
    }
  ];
  return cols.filter(c => c[filterBy] && !(Array.isArray(c.removeFromView) && c.removeFromView.includes(view) && filterBy !== 'publication'));
}
